import React, { useEffect, useRef, useState } from "react"
import { Head, useForm, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Mail, ArrowLeft, Shield } from "lucide-react"
import { useToast } from "@/hooks/use-toast"

export default function ForgotPassword({ flash = {}, errors = {} }) {
  const { data, setData, post, processing } = useForm({
    email: "",
  })
  const { toast } = useToast()
  const emailError = errors.email || flash?.error
  const emailSuccess = flash?.success
  const [isComplete, setIsComplete] = useState(false)
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState("")
  const redirectTimerRef = useRef(null)

  useEffect(() => {
    if (flash?.success) {
      toast({
        title: "Email sent",
        description: flash.success,
      })
    }

    if (flash?.error) {
      toast({
        title: "Unable to send reset email",
        description: flash.error,
        variant: "destructive",
      })
    }
  }, [flash, toast])

  useEffect(() => {
    if (flash?.success && !flash?.error) {
      setIsComplete(true)
      if (!redirectTimerRef.current) {
        redirectTimerRef.current = setTimeout(() => {
          router.visit("/login")
        }, 3000)
      }
    }
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current)
        redirectTimerRef.current = null
      }
    }
  }, [flash?.success, flash?.error])

  const submit = (e) => {
    e.preventDefault()
    setLastSubmittedEmail(data.email)
    post("/forgot-password", {
      preserveScroll: true,
      onError: (formErrors) => {
        if (formErrors?.email) {
          toast({
            title: "Invalid email",
            description: formErrors.email,
            variant: "destructive",
          })
        }
      },
    })
  }

  return (
    <>
      <Head title="DME | Forgot Password" />
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 dark:bg-gray-900">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <Shield className="mx-auto h-12 w-12 text-blue-600" />
            <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
              Forgot your password?
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Enter your email and we&apos;ll send you a reset link to that account.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Send reset link</CardTitle>
              <CardDescription>We&apos;ll email a link to reset your password.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={data.email}
                      onChange={(e) => setData("email", e.target.value)}
                      placeholder="name@example.com"
                      className="pl-10 h-10"
                      autoComplete="email"
                      required
                      disabled={processing || isComplete}
                    />
                  </div>
                  {emailError && <p className="text-sm text-destructive">{emailError}</p>}
                  {!emailError && emailSuccess && (
                    <p className="text-sm text-emerald-600">
                      {emailSuccess}
                      {lastSubmittedEmail && ` Check your email at ${lastSubmittedEmail}.`}
                    </p>
                  )}
                </div>

                {!isComplete ? (
                  <Button type="submit" className="w-full h-10 dark:text-white" disabled={processing}>
                    {processing ? "Sending..." : "Email reset link"}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Check your email at {lastSubmittedEmail || "your inbox"}. We&apos;ll redirect you to login shortly.
                    </p>
                    <Button
                      type="button"
                      className="w-full h-10 dark:text-white"
                      onClick={() => router.visit("/login")}
                    >
                      Back to login now
                    </Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between text-sm">
            <a href="/login" className="inline-flex items-center gap-1 text-primary hover:underline">
              <ArrowLeft className="h-4 w-4" />
              Back to login
            </a>
            <span className="text-muted-foreground text-xs">We never share your email.</span>
          </div>
        </div>
      </div>
    </>
  )
}

